<template>
  <div class="plans-public">

    <!-- Hero -->
    <div class="plans-hero">
      <div class="hero-inner">
        <div class="hero-eyebrow">Planos Golden Key</div>
        <div class="hero-title">Escolha o plano ideal para o seu negócio</div>
        <div class="hero-sub">Publique imóveis, alojamentos e viaturas rent-a-car com as ferramentas certas para crescer em Moçambique.</div>
      </div>
    </div>

    <div class="plans-wrapper">

      <!-- Loading -->
      <div v-if="loading" class="plans-grid">
        <div v-for="n in 3" :key="n" class="plan-card-skeleton">
          <q-skeleton type="rect" height="320px" style="border-radius:18px" />
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="plans.length === 0" class="empty-state">
        <q-icon name="layers" size="56px" color="grey-4" />
        <div class="empty-text">Nenhum plano disponível de momento. Contacte o suporte.</div>
      </div>

      <!-- Plans grid -->
      <div v-else class="plans-grid">
        <div
          v-for="plan in plans"
          :key="plan.id"
          class="plan-card"
          :class="{ 'plan-card--featured': plan.feature?.is_featured_home }"
        >
          <!-- Featured ribbon -->
          <div v-if="plan.feature?.is_featured_home" class="featured-ribbon">
            <q-icon name="star" size="12px" class="q-mr-xs" />
            Mais popular
          </div>

          <!-- Header -->
          <div class="plan-card-header">
            <div class="plan-name">{{ plan.name }}</div>
            <div v-if="plan.description" class="plan-desc">{{ plan.description }}</div>
          </div>

          <!-- Prices -->
          <div class="plan-prices">
            <div v-if="plan.is_negotiable" class="price-negotiable">
              <q-icon name="handshake" size="18px" class="q-mr-xs" />
              Preço sob consulta
            </div>
            <template v-else>
              <div v-for="price in sortedPrices(plan.prices)" :key="price.id" class="price-row">
                <div class="price-duration">{{ price.duration_days }} dias</div>
                <div class="price-amount">
                  <span class="price-currency">{{ price.currency }}</span>
                  <span class="price-value">{{ formatPrice(price.price) }}</span>
                </div>
              </div>
            </template>
          </div>

          <!-- Features -->
          <div class="plan-features">
            <div
              v-for="feature in catalogFeatures(plan)"
              :key="feature.listing_type"
              class="catalog-feature"
            >
              <div class="catalog-feature-title">
                <q-icon :name="catalogIcon(feature.listing_type)" size="15px" />
                {{ catalogLabel(feature.listing_type) }}
              </div>
              <div class="feature-row">
                <q-icon name="inventory_2" size="14px" class="feature-icon" />
                <span>Até <strong>{{ feature.max_active_listings }}</strong> anúncios activos</span>
              </div>
              <div class="feature-row">
                <q-icon name="photo_library" size="14px" class="feature-icon" />
                <span>Até <strong>{{ feature.max_photos }}</strong> fotos por anúncio</span>
              </div>
              <div v-if="feature.is_featured_home" class="feature-row feature-row--highlight">
                <q-icon name="star" size="14px" class="feature-icon" />
                <span>Destaque na página principal</span>
              </div>
              <div v-if="feature.shows_above_normal" class="feature-row feature-row--highlight">
                <q-icon name="trending_up" size="14px" class="feature-icon" />
                <span>Prioridade na listagem</span>
              </div>
              <div v-if="feature.auto_social_share" class="feature-row feature-row--highlight">
                <q-icon name="share" size="14px" class="feature-icon" />
                <span>Partilha automática nas redes sociais</span>
              </div>
              <div v-if="feature.whatsapp_button" class="feature-row feature-row--highlight">
                <q-icon name="chat" size="14px" class="feature-icon" />
                <span>Botão de contacto WhatsApp</span>
              </div>
            </div>
            <div v-if="plan.feature?.extra_slot_price && catalogFeatures(plan).length" class="feature-row">
              <q-icon name="add_circle" size="15px" class="feature-icon" />
              <span>Slots extra para os catálogos incluídos a {{ plan.feature.extra_slot_price }} MZN/cada</span>
            </div>
            <div v-if="additionalPerks(plan).length" class="additional-perks">
              <div class="catalog-feature-title">Benefícios adicionais</div>
              <div v-for="perk in additionalPerks(plan)" :key="perk" class="feature-row feature-row--perk">
                <q-icon name="check_circle" size="15px" class="feature-icon feature-icon--check" />
                <span>{{ perk }}</span>
              </div>
            </div>
          </div>

          <!-- CTA -->
          <div class="plan-cta">
            <template v-if="plan.is_negotiable">
              <a href="mailto:contacto@goldenkey.co.mz" class="cta-btn" :class="plan.feature?.is_featured_home ? 'cta-btn--featured' : ''">
                <q-icon name="email" size="16px" class="q-mr-xs" />
                Contactar para subscrever
              </a>
            </template>
            <template v-else-if="isAuthenticated">
              <button
                class="cta-btn cta-btn--mpesa"
                :class="plan.feature?.is_featured_home ? 'cta-btn--mpesa-featured' : ''"
                @click="openPayment(plan)"
              >
                <q-icon name="smartphone" size="16px" class="q-mr-xs" />
                Pagar com M-Pesa
              </button>
            </template>
            <template v-else>
              <router-link to="/login" class="cta-btn" :class="plan.feature?.is_featured_home ? 'cta-btn--featured' : ''">
                <q-icon name="login" size="16px" class="q-mr-xs" />
                Iniciar sessão para subscrever
              </router-link>
            </template>
          </div>
        </div>
      </div>

      <!-- Footer note -->
      <div class="plans-note">
        <q-icon name="info" size="16px" class="q-mr-xs" />
        Os preços estão em Meticais (MZN) e incluem acesso completo às funcionalidades do plano.
        Para planos Enterprise ou condições especiais, entre em contacto connosco.
      </div>
    </div>

    <!-- ── M-Pesa Payment Dialog ──────────────────────────── -->
    <q-dialog v-model="paymentDialog" persistent>
      <q-card class="pay-dialog">

        <!-- Header -->
        <div class="pay-header">
          <div class="pay-header-inner">
            <q-icon name="smartphone" size="20px" class="q-mr-sm" style="color:#d4af37" />
            <span class="pay-title">Subscrição com M-Pesa</span>
          </div>
          <q-btn flat round dense icon="close" color="white" @click="closePayment" :disable="payStep === 'waiting'" />
        </div>

        <!-- STEP: form -->
        <q-card-section v-if="payStep === 'form'" class="pay-body">

          <!-- Plan info (read-only) -->
          <div class="pay-plan-info">
            <div class="pay-plan-name">{{ payPlan?.name }}</div>
            <div v-if="payPlan?.description" class="pay-plan-desc">{{ payPlan.description }}</div>
          </div>

          <!-- Duration -->
          <div class="pay-label q-mt-md">Duração</div>
          <div v-if="!sortedPrices(payPlan?.prices).length" class="pay-empty">
            Nenhuma opção de duração disponível.
          </div>
          <div v-else class="duration-grid">
            <div
              v-for="price in sortedPrices(payPlan?.prices)"
              :key="price.id"
              class="duration-opt"
              :class="{ 'duration-opt--selected': payForm.duration?.id === price.id }"
              @click="payForm.duration = price"
            >
              <div class="dur-days">{{ price.duration_days }} dias</div>
              <div class="dur-price">
                {{ Number(price.price).toLocaleString('pt-MZ') }}
                <span class="dur-curr">{{ price.currency }}</span>
              </div>
            </div>
          </div>

          <div v-if="checkoutCatalogs.length && slotUnitPrice > 0" class="checkout-slots">
            <div class="pay-label q-mt-md">Adicionar slots extra (opcional)</div>
            <div class="slot-price-note">
              Cada slot custa {{ formatPrice(slotUnitPrice) }} {{ payForm.duration?.currency || 'MZN' }} e fica válido durante o plano.
            </div>
            <div
              v-for="catalog in checkoutCatalogs"
              :key="catalog.listing_type"
              class="slot-option"
            >
              <div class="slot-option-name">
                <q-icon :name="catalogIcon(catalog.listing_type)" size="17px" />
                {{ catalogLabel(catalog.listing_type) }}
              </div>
              <div class="slot-stepper">
                <q-btn
                  dense
                  round
                  outline
                  icon="remove"
                  color="grey-7"
                  :disable="extraSlotCount(catalog.listing_type) === 0"
                  :aria-label="`Reduzir slots de ${catalogLabel(catalog.listing_type)}`"
                  @click="adjustExtraSlots(catalog.listing_type, -1)"
                />
                <span class="slot-count">{{ extraSlotCount(catalog.listing_type) }}</span>
                <q-btn
                  dense
                  round
                  unelevated
                  icon="add"
                  color="positive"
                  :disable="extraSlotCount(catalog.listing_type) >= 100"
                  :aria-label="`Adicionar slot de ${catalogLabel(catalog.listing_type)}`"
                  @click="adjustExtraSlots(catalog.listing_type, 1)"
                />
              </div>
            </div>
          </div>

          <!-- M-Pesa number -->
          <div class="pay-label q-mt-md">Número M-Pesa</div>
          <q-input
            v-model="payForm.msisdn"
            outlined
            dense
            placeholder="841234567"
            maxlength="12"
            hint="Formato: 84/85XXXXXXX ou 258XXXXXXXXX"
          >
            <template v-slot:prepend>
              <q-icon name="smartphone" color="positive" size="18px" />
            </template>
          </q-input>

          <!-- Amount preview -->
          <div v-if="payForm.duration" class="amount-preview">
            <div class="amount-breakdown">
              <div class="amount-line">
                <span class="amount-label">Plano</span>
                <span>{{ formatPrice(payForm.duration.price) }} {{ payForm.duration.currency }}</span>
              </div>
              <div v-if="extraSlotsTotal > 0" class="amount-line">
                <span class="amount-label">Slots extra ({{ extraSlotsCount }})</span>
                <span>{{ formatPrice(extraSlotsTotal) }} {{ payForm.duration.currency }}</span>
              </div>
              <div class="amount-line amount-line--total">
                <span class="amount-label">Total a pagar</span>
                <span class="amount-value">{{ formatPrice(paymentTotal) }} {{ payForm.duration.currency }}</span>
              </div>
            </div>
          </div>
        </q-card-section>

        <!-- STEP: waiting -->
        <q-card-section v-else-if="payStep === 'waiting'" class="pay-body pay-centre">
          <q-spinner-oval color="positive" size="56px" />
          <div class="result-title q-mt-md">Aguarde…</div>
          <div class="result-text">
            Aprove o pagamento no menu M-Pesa do seu telemóvel.<br>
            <strong>{{ payForm.msisdn }}</strong>
          </div>
          <div class="waiting-note">
            <q-icon name="info" size="13px" class="q-mr-xs" />
            Esta janela ficará aberta até receber a confirmação.
          </div>
        </q-card-section>

        <q-card-section v-else-if="payStep === 'pending'" class="pay-body pay-centre">
          <q-icon name="hourglass_top" size="56px" color="warning" />
          <div class="result-title">Pagamento ainda não confirmado</div>
          <div class="result-text">{{ paymentError }}</div>
          <div class="waiting-note">Referência: {{ paymentReference }}</div>
        </q-card-section>

        <!-- STEP: paid -->
        <q-card-section v-else-if="payStep === 'paid'" class="pay-body pay-centre">
          <q-icon name="check_circle" size="64px" color="positive" />
          <div class="result-title" style="color:#16a34a">Subscrição activada!</div>
          <div class="result-text">Pagamento confirmado. A sua subscrição já está activa.</div>
        </q-card-section>

        <!-- STEP: failed -->
        <q-card-section v-else-if="payStep === 'failed'" class="pay-body pay-centre">
          <q-icon name="cancel" size="64px" color="negative" />
          <div class="result-title" style="color:#dc2626">Pagamento não efectuado</div>
          <div class="result-text">{{ paymentError }}</div>
        </q-card-section>

        <!-- Footer -->
        <q-card-actions align="right" class="pay-footer">
          <template v-if="payStep === 'form'">
            <q-btn flat label="Cancelar" @click="closePayment" color="grey-6" />
            <q-btn
              unelevated
              label="Pagar com M-Pesa"
              color="positive"
              :loading="submittingPayment"
              :disable="!payForm.duration || !payForm.msisdn"
              @click="submitPayment"
              style="border-radius:8px"
            />
          </template>
          <template v-else-if="payStep === 'waiting'" />
          <template v-else-if="payStep === 'pending'">
            <q-btn flat label="Fechar" @click="closePayment" color="grey-6" />
            <q-btn
              unelevated
              label="Verificar pagamento"
              icon="refresh"
              color="primary"
              :loading="checkingPayment"
              @click="checkPaymentStatus"
            />
          </template>
          <template v-else-if="payStep === 'paid'">
            <q-btn unelevated label="A minha subscrição" @click="goToSubscription" color="positive" style="border-radius:8px" />
          </template>
          <template v-else>
            <q-btn flat label="Fechar" @click="closePayment" color="grey-6" />
            <q-btn unelevated label="Tentar novamente" @click="payStep = 'form'" color="primary" style="border-radius:8px" />
          </template>
        </q-card-actions>

      </q-card>
    </q-dialog>

  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import store from '@/vuex/store';

export default {
  name: 'PlansPublic',

  setup() {
    const router = useRouter();
    const loading = ref(false);

    const plans = computed(() => store.state.plans.publicPlans);
    const isAuthenticated = computed(() => !!store.state.auth.user);

    const sortedPrices = (prices) => {
      if (!prices) return [];
      return [...prices].sort((a, b) => a.duration_days - b.duration_days);
    };

    const formatPrice = (val) => {
      if (val == null) return '—';
      return Number(val).toLocaleString('pt-MZ');
    };
    const catalogLabels = {
      property: 'Imóveis',
      accommodation: 'Alojamentos',
      rental_vehicle: 'Rent-a-car',
    };
    const catalogIcons = {
      property: 'home_work',
      accommodation: 'hotel',
      rental_vehicle: 'directions_car',
    };
    const catalogLabel = (type) => catalogLabels[type] || type;
    const catalogIcon = (type) => catalogIcons[type] || 'inventory_2';
    const catalogFeatures = (plan) =>
      (plan.listing_features || []).filter((feature) => feature.enabled);
    const additionalPerks = (plan) => {
      const catalogConfiguredPerks = /im[oó]vel|alojamento|viatura|fotograf|whatsapp|destaque|prioridade|resultados|partilha|publicação|tudo do plano|posição privilegiada/i;
      return (plan.feature?.perks || []).filter((perk) => !catalogConfiguredPerks.test(perk));
    };

    onMounted(async () => {
      loading.value = true;
      try {
        await store.dispatch('plans/getPublicPlans');
      } finally {
        loading.value = false;
      }
    });

    // ── M-Pesa payment ────────────────────────────────────
    const paymentDialog = ref(false);
    const payStep = ref('form');
    const paymentError = ref('');
    const submittingPayment = ref(false);
    const checkingPayment = ref(false);
    const paymentReference = ref('');
    const payPlan = ref(null);
    const payForm = ref({ duration: null, msisdn: '', extraSlots: {} });
    const checkoutCatalogs = computed(() => catalogFeatures(payPlan.value || {}));
    const slotUnitPrice = computed(() => Number(payPlan.value?.feature?.extra_slot_price || 0));
    const extraSlotsCount = computed(() =>
      Object.values(payForm.value.extraSlots || {}).reduce((total, quantity) => total + Number(quantity || 0), 0)
    );
    const extraSlotsTotal = computed(() => extraSlotsCount.value * slotUnitPrice.value);
    const paymentTotal = computed(() => Number(payForm.value.duration?.price || 0) + extraSlotsTotal.value);

    const openPayment = (plan) => {
      payPlan.value = plan;
      payStep.value = 'form';
      paymentError.value = '';
      paymentReference.value = '';
      payForm.value = { duration: null, msisdn: '', extraSlots: {} };
      const prices = sortedPrices(plan.prices);
      if (prices.length === 1) payForm.value.duration = prices[0];
      paymentDialog.value = true;
    };

    const extraSlotCount = (listingType) => Number(payForm.value.extraSlots[listingType] || 0);
    const adjustExtraSlots = (listingType, adjustment) => {
      const quantity = Math.max(0, Math.min(100, extraSlotCount(listingType) + adjustment));
      payForm.value.extraSlots = { ...payForm.value.extraSlots, [listingType]: quantity };
    };

    const closePayment = () => { paymentDialog.value = false; };

    const goToSubscription = () => {
      paymentDialog.value = false;
      router.push('/subscricao');
    };

    const submitPayment = async () => {
      submittingPayment.value = true;
      payStep.value = 'waiting';
      try {
        const res = await store.dispatch('subscriptions/initiatePayment', {
          plan_id: payPlan.value.id,
          duration_days: payForm.value.duration.duration_days,
          msisdn: payForm.value.msisdn.replace(/\D/g, ''),
          extra_slots: Object.entries(payForm.value.extraSlots)
            .filter(([, quantity]) => Number(quantity) > 0)
            .reduce((slots, [listingType, quantity]) => {
              slots[listingType] = Number(quantity);
              return slots;
            }, {}),
        });
        if (res.subscription) {
          store.commit('subscriptions/SET_MY_SUBSCRIPTION', res.subscription);
        }
        if (res.status === 'paid' && res.subscription) {
          payStep.value = 'paid';
        } else if (res.status === 'pending' && res.reference) {
          paymentReference.value = res.reference;
          paymentError.value = res.message || 'Não faça outro pagamento enquanto o M-Pesa confirma esta transacção.';
          payStep.value = 'pending';
        } else {
          throw new Error(res.message || 'Não foi possível confirmar o estado do pagamento.');
        }
      } catch (err) {
        paymentError.value = err.response?.data?.message || err.message || 'Erro ao processar pagamento. Tente novamente.';
        payStep.value = 'failed';
      } finally {
        submittingPayment.value = false;
      }
    };

    const checkPaymentStatus = async () => {
      if (!paymentReference.value) return;
      checkingPayment.value = true;
      try {
        const result = await store.dispatch('subscriptions/getPaymentStatus', paymentReference.value);
        if (result.status === 'paid') {
          const subscription = await store.dispatch('subscriptions/getMySubscription');
          if (!subscription) {
            throw new Error('O pagamento foi confirmado, mas a subscrição ainda não está disponível.');
          }
          payStep.value = 'paid';
        } else if (result.status === 'failed') {
          paymentError.value = 'O pagamento foi recusado ou expirou. Pode iniciar novamente.';
          payStep.value = 'failed';
        } else {
          paymentError.value = 'O M-Pesa ainda não confirmou o pagamento. Consulte novamente antes de tentar outra vez.';
        }
      } catch (err) {
        paymentError.value = err.response?.data?.message || err.message || 'Não foi possível consultar o pagamento.';
      } finally {
        checkingPayment.value = false;
      }
    };
    // ─────────────────────────────────────────────────────

    return {
      loading, plans, isAuthenticated,
      sortedPrices, formatPrice, catalogFeatures, catalogLabel, catalogIcon, additionalPerks,
      paymentDialog, payStep, paymentError, submittingPayment, checkingPayment, paymentReference,
      payPlan, payForm, checkoutCatalogs, slotUnitPrice, extraSlotsCount, extraSlotsTotal, paymentTotal,
      extraSlotCount, adjustExtraSlots,
      openPayment, closePayment, goToSubscription, submitPayment, checkPaymentStatus,
    };
  },
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────── */
.plans-public { background: #f4f6fb; min-height: 100vh; }

/* ── Hero ─────────────────────────────────────────────── */
.plans-hero {
  background: linear-gradient(135deg, #0d1b2a 0%, #1a3353 100%);
  padding: 60px 24px;
  text-align: center;
}
.hero-inner { max-width: 600px; margin: 0 auto; }
.hero-eyebrow {
  display: inline-block; font-size: 11px; font-weight: 700;
  color: #d4af37; letter-spacing: 2px; text-transform: uppercase;
  border: 1px solid rgba(212,175,55,.3); border-radius: 20px;
  padding: 4px 16px; margin-bottom: 16px;
}
.hero-title { font-size: 28px; font-weight: 800; color: #fff; line-height: 1.3; margin-bottom: 12px; }
.hero-sub { font-size: 15px; color: rgba(255,255,255,.6); line-height: 1.6; }

/* ── Plans wrapper ────────────────────────────────────── */
.plans-wrapper { max-width: 1100px; margin: 0 auto; padding: 48px 16px 60px; }

/* ── Plans grid ───────────────────────────────────────── */
.plans-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px; margin-bottom: 40px; }
.plan-card-skeleton { border-radius: 18px; overflow: hidden; }

/* ── Plan card ────────────────────────────────────────── */
.plan-card {
  background: #fff; border-radius: 18px; border: 1px solid #e8ecf4;
  overflow: hidden; display: flex; flex-direction: column;
  transition: box-shadow .2s, transform .2s; position: relative;
}
.plan-card:hover { box-shadow: 0 8px 32px rgba(13,27,42,.1); transform: translateY(-3px); }
.plan-card--featured {
  border-color: #d4af37; box-shadow: 0 4px 24px rgba(212,175,55,.15);
}

/* ── Featured ribbon ──────────────────────────────────── */
.featured-ribbon {
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(90deg, #d4af37, #e2c252);
  color: #0d1b2a; font-size: 11px; font-weight: 800;
  letter-spacing: .5px; text-transform: uppercase; padding: 6px 16px;
}

/* ── Card header ──────────────────────────────────────── */
.plan-card-header { padding: 24px 24px 16px; border-bottom: 1px solid #f1f5f9; }
.plan-name { font-size: 20px; font-weight: 800; color: #0d1b2a; margin-bottom: 4px; }
.plan-desc { font-size: 13px; color: #64748b; line-height: 1.5; }

/* ── Prices ───────────────────────────────────────────── */
.plan-prices { padding: 16px 24px; border-bottom: 1px solid #f1f5f9; }
.price-negotiable {
  display: flex; align-items: center; font-size: 15px; font-weight: 700;
  color: #d4af37; font-style: italic;
}
.price-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 0; border-bottom: 1px dashed #f1f5f9;
}
.price-row:last-child { border-bottom: none; }
.price-duration { font-size: 12px; color: #94a3b8; font-weight: 600; }
.price-amount { display: flex; align-items: baseline; gap: 4px; }
.price-currency { font-size: 11px; color: #64748b; font-weight: 600; }
.price-value { font-size: 20px; font-weight: 800; color: #0d1b2a; }

/* ── Features ─────────────────────────────────────────── */
.plan-features { padding: 16px 24px; flex: 1; }
.catalog-feature { padding: 10px 0; border-bottom: 1px solid #eef2f7; }
.catalog-feature:last-of-type { border-bottom: 0; }
.catalog-feature-title {
  display: flex; align-items: center; gap: 7px; margin-bottom: 8px;
  color: #0d1b2a; font-size: 13px; font-weight: 800;
}
.catalog-feature-title .q-icon { color: #c69e26; }
.additional-perks { margin-top: 10px; padding-top: 12px; border-top: 1px solid #eef2f7; }
.feature-row {
  display: flex; align-items: flex-start; gap: 8px;
  font-size: 13px; color: #475569; margin-bottom: 8px; line-height: 1.4;
}
.feature-row--highlight { color: #0f172a; font-weight: 500; }
.feature-row--perk { color: #16a34a; }
.feature-icon { color: #94a3b8; flex-shrink: 0; margin-top: 1px; }
.feature-icon--check { color: #16a34a; }

/* ── CTA ──────────────────────────────────────────────── */
.plan-cta { padding: 16px 24px 24px; }
.cta-btn {
  display: flex; align-items: center; justify-content: center;
  width: 100%; padding: 12px 20px; border-radius: 10px;
  font-size: 13.5px; font-weight: 700; text-decoration: none;
  letter-spacing: .3px; transition: background .15s, transform .1s;
  background: #0d1b2a; color: #fff;
}
.cta-btn:hover { background: #1a3353; transform: scale(1.01); }
.cta-btn--featured { background: #d4af37; color: #0d1b2a; }
.cta-btn--featured:hover { background: #c9a42d; }

/* ── Note ─────────────────────────────────────────────── */
.plans-note {
  display: flex; align-items: flex-start; gap: 4px;
  background: rgba(59,130,246,.06); border: 1px solid rgba(59,130,246,.15);
  border-radius: 12px; padding: 16px 20px; font-size: 13px; color: #475569;
  line-height: 1.6;
}

/* ── Empty ────────────────────────────────────────────── */
.empty-state { text-align: center; padding: 64px 16px; }
.empty-text { font-size: 15px; color: #94a3b8; margin-top: 12px; }

/* ── M-Pesa CTA buttons ───────────────────────────────── */
.cta-btn--mpesa {
  display: flex; align-items: center; justify-content: center;
  width: 100%; padding: 12px 20px; border-radius: 10px; border: none;
  font-size: 13.5px; font-weight: 700; letter-spacing: .3px;
  cursor: pointer; transition: background .15s, transform .1s;
  background: #16a34a; color: #fff;
}
.cta-btn--mpesa:hover { background: #15803d; transform: scale(1.01); }
.cta-btn--mpesa-featured { background: #0d7a3a; }

/* ── Payment dialog ───────────────────────────────────── */
.pay-dialog { width: 100%; max-width: 520px; max-height: 90vh; border-radius: 18px !important; overflow: hidden; }
.pay-header {
  background: #0d1b2a; display: flex; align-items: center;
  justify-content: space-between; padding: 16px 20px;
}
.pay-header-inner { display: flex; align-items: center; }
.pay-title { font-size: 16px; font-weight: 700; color: #fff; }
.pay-body { padding: 24px; max-height: calc(90vh - 130px); overflow-y: auto; }
.pay-footer { background: #f8fafc; border-top: 1px solid #e8ecf4; padding: 14px 20px; }
.pay-label {
  font-size: 11px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: .8px; margin-bottom: 10px;
}
.pay-empty { font-size: 13px; color: #94a3b8; text-align: center; padding: 16px; }

.pay-plan-info {
  background: #f8fafc; border: 1px solid #e8ecf4;
  border-radius: 12px; padding: 14px 16px; margin-bottom: 4px;
}
.pay-plan-name { font-size: 16px; font-weight: 800; color: #0d1b2a; }
.pay-plan-desc { font-size: 13px; color: #64748b; margin-top: 3px; }

.duration-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.duration-opt {
  flex: 1; min-width: 110px; padding: 12px 14px; text-align: center;
  border: 1.5px solid #e2e8f0; border-radius: 12px; cursor: pointer;
  transition: border-color .15s, background .15s;
}
.duration-opt:hover { border-color: #94a3b8; background: #f8fafc; }
.duration-opt--selected { border-color: #16a34a; background: rgba(22,163,74,.05); }
.dur-days { font-size: 12px; font-weight: 600; color: #64748b; }
.dur-price { font-size: 16px; font-weight: 800; color: #0f172a; margin-top: 4px; }
.dur-curr { font-size: 11px; font-weight: 500; color: #94a3b8; }

.checkout-slots {
  margin-top: 18px; padding: 14px; border: 1px solid #e8ecf4;
  border-radius: 12px; background: #fbfcfe;
}
.slot-price-note { margin: -4px 0 8px; color: #64748b; font-size: 12px; line-height: 1.5; }
.slot-option {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 8px 0; border-top: 1px solid #eef2f7;
}
.slot-option-name { display: flex; align-items: center; gap: 8px; color: #334155; font-size: 13px; font-weight: 600; }
.slot-option-name .q-icon { color: #c69e26; }
.slot-stepper { display: flex; align-items: center; gap: 12px; }
.slot-count { min-width: 18px; text-align: center; color: #0d1b2a; font-size: 14px; font-weight: 800; }

.amount-preview {
  background: rgba(22,163,74,.08); border: 1px solid rgba(22,163,74,.2);
  border-radius: 10px; padding: 12px 16px; margin-top: 16px;
}
.amount-breakdown { display: grid; gap: 5px; }
.amount-line { display: flex; align-items: center; justify-content: space-between; gap: 12px; color: #334155; font-size: 13px; }
.amount-line--total { margin-top: 5px; padding-top: 8px; border-top: 1px solid rgba(22,163,74,.2); }
.amount-label { font-size: 13px; font-weight: 600; color: #64748b; }
.amount-value { font-size: 18px; font-weight: 800; color: #16a34a; }

.pay-centre { text-align: center; padding: 36px 24px; }
.result-title { font-size: 20px; font-weight: 800; margin: 12px 0 8px; }
.result-text { font-size: 13.5px; color: #64748b; line-height: 1.6; }
.waiting-note {
  display: inline-flex; align-items: center; margin-top: 20px;
  font-size: 12px; color: #94a3b8; background: #f8fafc;
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 14px;
}
</style>
